import { useRef, useState } from "react";
import {
  Archive,
  Loader2,
  RefreshCw,
  Trash2,
  UserMinus,
  UserPlus,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BulkActionsBar as Shell } from "@/components/ui/BulkActionsBar";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { ArchiveCompanyDialog } from "./ArchiveCompanyDialog";
import { bulkAction, bulkDeleteCompanies } from "@/api/companies";
import type {
  BulkActionName,
  PipelineStatus,
  ProjectMember,
  User,
} from "@/api/types";

interface BulkActionsBarProps {
  projectSlug: string;
  pipelineStatuses: PipelineStatus[];
  members: ProjectMember[];
  selectedIds: number[];
  currentUser: User | null;
  /** When true, ignore `selectedIds` and ask the server to mutate
   *  every row matching `allMatchingFilters`. Used by the "select all
   *  matching" banner so the client doesn't paginate to collect ids. */
  selectAllMatching?: boolean;
  allMatchingFilters?: Record<string, unknown>;
  /** When in all_matching mode, used for the success toast since the
   *  server's response is the only authoritative count. Falls back to
   *  the response's `updated`. */
  totalMatching?: number;
  /** Renders Change-status + Assign actions when true. */
  canEdit?: boolean;
  /** Renders the Archive action when true. */
  canArchive?: boolean;
  /** Renders the Delete action when true. Used on the Archived
   *  tab where archiving is a no-op and the natural destructive
   *  affordance is permanent deletion. Gated behind a typed-count
   *  confirm because it cannot be undone. */
  canDelete?: boolean;
  onClear: () => void;
  onSuccess: () => void;
}

export function BulkActionsBar({
  projectSlug,
  pipelineStatuses,
  members,
  selectedIds,
  currentUser,
  selectAllMatching = false,
  allMatchingFilters,
  totalMatching,
  canEdit = false,
  canArchive = false,
  canDelete = false,
  onClear,
  onSuccess,
}: BulkActionsBarProps) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState<null | "status" | "owner" | "delete">(null);
  // Synchronous lock — `busy` is state and so updates are deferred
  // until the next render; if two events fire in the same tick (a
  // double-tap on touch, a synthetic re-dispatch, etc.) both would
  // observe `busy === null` and both would POST. A ref blocks the
  // second call immediately.
  const submittingRef = useRef(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  // Count shown on the bar's chip — in all_matching mode the
  // selectedIds set is just whatever's visible on the current page, so
  // it'd lie about the operation's scope. Use the server total.
  const count = selectAllMatching
    ? totalMatching ?? selectedIds.length
    : selectedIds.length;

  async function applyAll(
    label: string,
    action: BulkActionName,
    payload: Record<string, unknown>,
    kind: "status" | "owner",
  ) {
    if (submittingRef.current) return;

    // Safety rails for the all_matching path — operators have hit
    // production rows by accident when the matching set was much
    // larger than the visible selection. We log what's about to ship
    // and require an explicit confirmation that scales with the
    // blast radius of the action.
    if (selectAllMatching) {
      const filtersOut = allMatchingFilters ?? {};
      const matchCount = totalMatching;
       
      console.warn("[BulkAction] all_matching about to fire", {
        action,
        payload,
        filters: filtersOut,
        totalMatching: matchCount,
      });

      if (matchCount == null) {
        toast.error(
          "Couldn't determine how many companies match, refresh and retry.",
        );
        return;
      }

      if (action === "change_status") {
        // High-blast-radius action: require the operator to type the
        // exact match count. Catches the case where the filter set is
        // wider than the operator believed.
        const ok = await confirm({
          title: "Bulk Status Change",
          description: `This will ${label} for ${matchCount.toLocaleString()} companies matching the current filters. This action cannot be easily undone.`,
          confirmLabel: "Change Status",
          variant: "danger",
          icon: <RefreshCw className="h-5 w-5" />,
          requireInput: String(matchCount),
          requireInputLabel: `Type ${matchCount} to confirm`,
        });
        if (!ok) return;
      } else {
        // Lower-blast actions (assign_owner) still get a confirm
        // step — the typed-count gate would be overkill, but a
        // silent fire isn't safe either.
        const ok = await confirm({
          title: "Bulk Assignment",
          description: `Apply this to ${matchCount.toLocaleString()} companies matching the current filters?`,
          confirmLabel: "Apply",
          variant: "warning",
        });
        if (!ok) return;
      }
    } else {
       
      console.log("[BulkAction] selected", {
        action,
        count: selectedIds.length,
      });
    }

    submittingRef.current = true;
    setBusy(kind);
    try {
      const res = await bulkAction({
        slug: projectSlug,
        mode: selectAllMatching ? "all_matching" : "selected",
        companyIds: selectAllMatching ? undefined : selectedIds,
        filters: selectAllMatching ? allMatchingFilters ?? {} : undefined,
        action,
        payload,
      });
      const { affected, skipped } = res;
      if (affected === 0 && skipped.length === 0) {
        // Server saw nothing to act on — empty selection, all rows
        // already at the target value, or a no-op payload.
        toast.info("No changes applied");
      } else if (skipped.length > 0) {
        toast.warning(
          `${affected} compan${affected === 1 ? "y" : "ies"} ${label} · ${skipped.length} skipped`,
        );
      } else {
        toast.success(
          `${affected} compan${affected === 1 ? "y" : "ies"} ${label}`,
        );
      }
      onSuccess();
      onClear();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      submittingRef.current = false;
      setBusy(null);
    }
  }

  /** Permanent deletion of selected rows. Used only on the Archived
   *  tab — the natural place for "this row is gone, archive it"
   *  and "this row is REALLY gone, delete it" to live together.
   *  Cannot be undone; gated behind a typed-count confirm whose
   *  required input is the operation's exact blast-radius count. */
  async function handleBulkDelete() {
    if (submittingRef.current) return;
    // /bulk-delete (like /bulk-archive) only accepts an id list;
    // the all_matching path can't safely target rows the client
    // hasn't paginated to. The button itself is disabled in that
    // mode, but this is the belt-and-braces guard.
    if (selectAllMatching) return;
    const ids = selectedIds;
    if (ids.length === 0) return;

    const ok = await confirm({
      title: `Delete ${ids.length} compan${ids.length === 1 ? "y" : "ies"}?`,
      description:
        "This permanently removes the selected companies along with their contacts, activities, and attachments. It cannot be undone.",
      confirmLabel: "Delete permanently",
      variant: "danger",
      icon: <Trash2 className="h-5 w-5" />,
      requireInput: String(ids.length),
      requireInputLabel: `Type ${ids.length} to confirm`,
    });
    if (!ok) return;

    console.warn("[BulkAction] bulk-delete about to fire", {
      count: ids.length,
      ids,
    });

    submittingRef.current = true;
    setBusy("delete");
    try {
      const res = await bulkDeleteCompanies(projectSlug, ids);
      const { deleted, skipped } = res;
      if (deleted === 0 && skipped.length === 0) {
        toast.info("Nothing to delete.");
      } else if (skipped.length > 0) {
        toast.warning(
          `${deleted} compan${deleted === 1 ? "y" : "ies"} deleted · ${skipped.length} skipped`,
        );
      } else {
        toast.success(
          `${deleted} compan${deleted === 1 ? "y" : "ies"} deleted`,
        );
      }
      onSuccess();
      onClear();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      submittingRef.current = false;
      setBusy(null);
    }
  }

  return (
    <Shell count={count} onClear={onClear}>
      {canEdit && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={busy !== null}>
              {busy === "status" && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Change status
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center">
            <DropdownMenuLabel>Set status to</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {pipelineStatuses
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((s) => (
                <DropdownMenuItem
                  key={s.key}
                  onSelect={() =>
                    applyAll(
                      `set to ${s.label}`,
                      "change_status",
                      { pipeline_status: s.key },
                      "status",
                    )
                  }
                >
                  {s.label}
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {canEdit && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={busy !== null}>
              {busy === "owner" ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <UserPlus className="mr-2 h-3.5 w-3.5" />
              )}
              Assign
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="min-w-[200px]">
            <DropdownMenuLabel>Owner</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() =>
                applyAll(
                  "unassigned",
                  "assign_owner",
                  { owner_id: null },
                  "owner",
                )
              }
            >
              <UserMinus className="mr-2 h-3.5 w-3.5" />
              Unassign
            </DropdownMenuItem>
            {members.length > 0 && <DropdownMenuSeparator />}
            {members.map((m) => {
              const isMe = currentUser && m.id === currentUser.id;
              return (
                <DropdownMenuItem
                  key={m.id}
                  onSelect={() =>
                    applyAll(
                      `assigned to ${isMe ? "you" : m.full_name}`,
                      "assign_owner",
                      { owner_id: m.id },
                      "owner",
                    )
                  }
                >
                  <UserPlus className="mr-2 h-3.5 w-3.5" />
                  <span className="flex-1 truncate">
                    {m.full_name}
                    {isMe && (
                      <span className="ml-1.5 text-xs text-slate-400">
                        (you)
                      </span>
                    )}
                  </span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {canArchive && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setArchiveOpen(true)}
          // /bulk-archive only accepts an id list — in all_matching
          // mode selectedIds is just the visible page, so archiving
          // would silently apply to a subset. Lock it out and ask the
          // operator to clear or use selected-rows mode instead.
          disabled={busy !== null || selectAllMatching}
          title={
            selectAllMatching
              ? "Clear the selection to archive individually picked rows."
              : undefined
          }
          className="border-amber-200 text-amber-700 hover:bg-amber-50"
        >
          <Archive className="mr-2 h-3.5 w-3.5" />
          Archive
        </Button>
      )}

      {canDelete && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void handleBulkDelete()}
          // Same all_matching constraint as Archive: /bulk-delete
          // takes an id list, not a filter set. Belt-and-braces
          // alongside the guard inside handleBulkDelete.
          disabled={busy !== null || selectAllMatching}
          title={
            selectAllMatching
              ? "Clear the selection to delete individually picked rows."
              : undefined
          }
          className="border-rose-200 text-rose-700 hover:bg-rose-50"
        >
          {busy === "delete" ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="mr-2 h-3.5 w-3.5" />
          )}
          Delete
        </Button>
      )}

      <ArchiveCompanyDialog
        mode="bulk"
        projectSlug={projectSlug}
        ids={selectedIds}
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        onArchived={() => {
          onSuccess();
          onClear();
        }}
      />
    </Shell>
  );
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Something went wrong.";
}
